import React from 'react';
import { ChevronLeft, Zap, Target, Users, TrendingUp, Handshake } from 'lucide-react';
import { motion } from 'framer-motion';
interface FoundHerExperienceProps {
  onNavigate: () => void;
}
const headerImages = ['https://cdn.magicpatterns.com/uploads/wUTGPLApb6amEUo4toJk4K/pexels-george-milton-6954180.jpg', 'https://cdn.magicpatterns.com/uploads/5Q6pfPPsTVz9TsThyUNBuG/pexels-george-milton-6953819.jpg', 'https://cdn.magicpatterns.com/uploads/5vHU7bD59qD7HKDskNVHEB/pexels-rdne-7490981.jpg', 'https://cdn.magicpatterns.com/uploads/63c112f1dQYn2EZjkcWzTn/pexels-rdne-7490837.jpg'];
const timeline = [{
  time: '4:00 PM',
  title: 'Arrival & Vibes',
  desc: 'Music. Light bites. Intention.'
}, {
  time: '4:20 PM',
  title: 'Leadership & Partners Address',
  desc: 'Opening remarks from leadership and partners setting the vision for the evening.'
}, {
  time: '4:30 PM',
  title: 'Investor Insights Panel',
  desc: 'What makes them write a cheque. What founders get wrong. What makes a pitch unforgettable.'
}, {
  time: '5:00 PM',
  title: 'Speed Investor Matching',
  desc: '5–7 minute rapid-fire meetings. Curated rotations. Direct fundraising conversations.'
}, {
  time: '6:15 PM',
  title: '2-Minute Stage Pitch',
  desc: 'Clear problem. Big vision. Strong traction. Direct ask.'
}, {
  time: '7:15 PM',
  title: 'Open Networking & Celebration',
  desc: 'Follow-ups begin. Conversations deepen. Deals start forming.'
}];
const metrics = [{
  icon: Target,
  text: 'Investor follow-ups'
}, {
  icon: Zap,
  text: 'Active deal flow'
}, {
  icon: TrendingUp,
  text: 'Fundraising momentum'
}, {
  icon: Handshake,
  text: 'Long-term partnerships'
}, {
  icon: Users,
  text: 'Stronger collaboration across the ecosystem'
}];
export function FoundHerExperience({
  onNavigate
}: FoundHerExperienceProps) {
  return <div className="min-h-screen flex flex-col w-full max-w-lg md:max-w-3xl lg:max-w-5xl mx-auto relative z-10 pb-12 md:pb-20" data-id="element-60">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel px-4 md:px-8 py-4 md:py-6 mb-6 md:mb-8" data-id="element-61">
        <div className="flex items-center" data-id="element-62">
          <button onClick={onNavigate} className="p-2 -ml-2 mr-2 md:mr-4 rounded-full hover:bg-white/10 transition-colors text-white/80" aria-label="Back to home" data-id="element-63">
            <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" data-id="element-64" />
          </button>
          <h1 className="font-sora font-bold text-xl md:text-2xl lg:text-3xl text-white" data-id="element-65">
            FoundHer Experience
          </h1>
        </div>
      </header>

      <main className="px-6 md:px-8 space-y-16 md:space-y-24" data-id="element-66">
        {/* Hero Image — Full width, tall */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7
      }} className="relative overflow-hidden rounded-3xl aspect-[16/9] md:aspect-[2/1] -mt-2 md:mt-0" data-id="element-67">
          <img src={headerImages[0]} alt="FoundHer Experience" className="w-full h-full object-cover object-[center_25%]" data-id="element-68" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e27]/70 via-[#0a0e27]/20 to-transparent" data-id="element-69" />
        </motion.div>

        {/* Intro */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center space-y-4 md:space-y-6 mt-4 md:mt-8" data-id="element-70">
          <h2 className="font-sora font-bold text-2xl md:text-4xl lg:text-5xl text-transparent bg-clip-text bg-gradient-to-r from-accent-pink to-accent-purple leading-tight" data-id="element-71">
            You're not only here to network,
            <br data-id="element-72" />
            you're also here to raise.
          </h2>
          <p className="font-inter text-white/80 leading-relaxed md:text-lg lg:text-xl max-w-2xl mx-auto" data-id="element-73">
            This room is built for momentum, visibility, and real capital
            connection.
          </p>
        </motion.section>

        {/* Second Image — Two-up side by side */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.6
      }} className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-id="element-74">
          <div className="relative overflow-hidden rounded-3xl aspect-[4/5]" data-id="element-75">
            <img src={headerImages[1]} alt="FoundHer Experience" className="w-full h-full object-cover" data-id="element-76" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e27]/50 via-transparent to-transparent" data-id="element-77" />
          </div>
          <div className="relative overflow-hidden rounded-3xl aspect-[4/5]" data-id="element-78">
            <img src={headerImages[2]} alt="FoundHer Experience" className="w-full h-full object-cover" data-id="element-79" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e27]/50 via-transparent to-transparent" data-id="element-80" />
          </div>
        </motion.div>

        {/* Timeline */}
        <section data-id="element-81">
          <h3 className="font-sora font-semibold text-lg md:text-2xl text-white mb-8 md:mb-12 flex items-center" data-id="element-82">
            <span className="w-8 md:w-12 h-[1px] md:h-[2px] bg-accent-pink mr-4" data-id="element-83"></span>
            Event Flow
          </h3>
          <div className="relative pl-6 md:pl-8 border-l border-white/10 lg:border-none lg:pl-0 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6" data-id="element-84">
            {timeline.map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            delay: index * 0.1
          }} className="relative lg:bg-white/5 lg:border lg:border-white/10 lg:rounded-2xl lg:p-6 lg:hover:bg-white/10 lg:transition-colors" data-id="element-85">
                {/* Glowing dot - only visible on mobile/tablet where the vertical line exists */}
                <div className="absolute -left-[29px] md:-left-[37px] lg:hidden top-1.5 md:top-2 w-3 h-3 md:w-4 md:h-4 rounded-full bg-accent-pink shadow-[0_0_10px_rgba(224,64,251,0.8)] border-2 border-[#0a0e27]" data-id="element-86" />

                <div className="glass-card rounded-xl p-4 md:p-6 lg:p-0 lg:bg-transparent lg:border-none lg:shadow-none lg:backdrop-blur-none" data-id="element-87">
                  <span className="font-sora text-xs md:text-sm lg:text-base font-bold text-accent-pink tracking-wider mb-1 md:mb-2 block" data-id="element-88">
                    {item.time}
                  </span>
                  <h4 className="font-sora font-semibold text-white md:text-xl mb-2" data-id="element-89">
                    {item.title}
                  </h4>
                  <p className="font-inter text-sm md:text-base lg:text-lg text-white/70" data-id="element-90">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </section>

        {/* Third Image — Full width cinematic */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.98
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.7
      }} className="relative overflow-hidden rounded-3xl aspect-[16/9] md:aspect-[21/9]" data-id="element-91">
          <img src={headerImages[3]} alt="FoundHer Experience" className="w-full h-full object-cover" data-id="element-92" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e27]/60 via-transparent to-transparent" data-id="element-93" />
        </motion.div>

        {/* Why This Matters */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="glass-card rounded-2xl p-6 md:p-10 lg:p-12 relative overflow-hidden" data-id="element-94">
          <div className="absolute top-0 right-0 w-32 md:w-64 h-32 md:h-64 bg-accent-purple/20 rounded-full blur-[50px] md:blur-[80px] -z-10" data-id="element-95" />
          <h3 className="font-sora font-bold text-xl md:text-3xl text-white mb-4 md:mb-6" data-id="element-96">
            Why This Event Matters
          </h3>
          <p className="font-inter text-white/90 font-medium mb-6 md:mb-8 text-lg md:text-2xl max-w-3xl" data-id="element-97">
            Access to capital doesn't happen by accident. It happens by design.
          </p>
          <p className="font-inter text-white/70 text-sm md:text-base lg:text-lg mb-4 md:mb-6" data-id="element-98">
            This experience is built to:
          </p>
          <ul className="space-y-3 md:space-y-4 font-inter text-sm md:text-base lg:text-lg text-white/80" data-id="element-99">
            <li className="flex items-start" data-id="element-100">
              <span className="text-accent-pink mr-3 mt-0.5 md:mt-1" data-id="element-101">•</span>
              Create structured investor access
            </li>
            <li className="flex items-start" data-id="element-102">
              <span className="text-accent-pink mr-3 mt-0.5 md:mt-1" data-id="element-103">•</span>
              Remove barriers to meaningful conversations
            </li>
            <li className="flex items-start" data-id="element-104">
              <span className="text-accent-pink mr-3 mt-0.5 md:mt-1" data-id="element-105">•</span>
              Put high-growth founders in front of decision-makers
            </li>
            <li className="flex items-start" data-id="element-106">
              <span className="text-accent-pink mr-3 mt-0.5 md:mt-1" data-id="element-107">•</span>
              Strengthen Canada's innovation ecosystem
            </li>
          </ul>
        </motion.section>

        {/* Success Metrics */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} data-id="element-108">
          <h3 className="font-sora font-semibold text-lg md:text-2xl text-white mb-6 md:mb-8 text-center" data-id="element-109">
            Success Metrics
          </h3>
          <div className="grid gap-3 md:gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3" data-id="element-110">
            {metrics.map((metric, index) => <div key={index} className="flex items-center glass-card rounded-lg p-3 md:p-4" data-id="element-111">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-accent-pink/10 flex items-center justify-center mr-3 md:mr-4 flex-shrink-0" data-id="element-112">
                  <metric.icon className="w-4 h-4 md:w-5 md:h-5 text-accent-pink" data-id="element-113" />
                </div>
                <span className="font-inter text-sm md:text-base lg:text-lg text-white/90 font-medium" data-id="element-114">
                  {metric.text}
                </span>
              </div>)}
          </div>
        </motion.section>

        {/* Atmosphere */}
        <motion.section initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="text-center py-10 md:py-16 border-t border-b border-white/10" data-id="element-115">
          <h3 className="font-sora font-bold text-2xl md:text-4xl lg:text-5xl text-white mb-4 md:mb-6 uppercase tracking-wide leading-tight" data-id="element-116">
            High-energy.
            <br data-id="element-117" />
            Zero fluff.
            <br data-id="element-118" />
            Curated access.
            <br data-id="element-119" />
            Real capital.
          </h3>
          <p className="font-inter text-accent-pink font-medium md:text-xl lg:text-2xl" data-id="element-120">
            This is where momentum begins.
          </p>
        </motion.section>
      </main>
    </div>;
}